import { UserDisplayNameEntity } from 'Models/Entities';
import { getMessageByKey } from 'Stores/LanguageStore';
import { slugifyLabel } from 'Modules/DynamicBookingFields/Utils/translationKeyPrefill';

/**
 * Base translation keys for the request-account-type flow. These ship in
 * default-translations.json, so every language already has them.
 */
export const userTypeRequestTextKeys = {
	title: 'user-type-request__modal__title',
	typeLabel: 'user-type-request__type__label',
	typePlaceholder: 'user-type-request__type__placeholder',
	messageLabel: 'user-type-request__message__label',
	messagePlaceholder: 'user-type-request__message__placeholder',
	submit: 'user-type-request__submit__label',
	submitPending: 'user-type-request__submit__pending-label',
	cancel: 'user-type-request__cancel__label',
	sendFailed: 'user-type-request__send-failed__message',
} as const;

export type UserTypeRequestTextName = keyof typeof userTypeRequestTextKeys;

/**
 * Used when a language has no entry for the base key at all — e.g. a language
 * created before these keys existed and never re-seeded.
 */
const fallbacks: Record<UserTypeRequestTextName, string> = {
	title: 'Request account type',
	typeLabel: 'Account type',
	typePlaceholder: 'Select an account type',
	messageLabel: 'Message',
	messagePlaceholder: 'Anything staff should know about this request (optional)',
	submit: 'Send request',
	submitPending: 'Sending…',
	cancel: 'Cancel',
	sendFailed: 'The request could not be sent right now. Please try again.',
};

/**
 * The per-type override key for a base key: the base key suffixed with the type's
 * slugified display name, e.g. `user-type-request__message__placeholder__freight-customer`.
 * Slugged the same way as the dynamic booking field keys so operators only learn one rule.
 */
export function userTypeRequestOverrideKey(name: UserTypeRequestTextName, type?: UserDisplayNameEntity) {
	const slug = slugifyLabel(type?.displayName);
	return slug ? `${userTypeRequestTextKeys[name]}__${slug}` : null;
}

/**
 * Resolves one of the request-account-type strings for the current language, preferring a
 * per-type override when the selected type has one. Operators add overrides in the Languages
 * editor — there's no override until they do, so this falls back to the shared wording.
 *
 * A present-but-empty override is honoured (an operator blanking a placeholder means it), only
 * a missing key falls through.
 */
export function getUserTypeRequestText(name: UserTypeRequestTextName, type?: UserDisplayNameEntity): string {
	const overrideKey = userTypeRequestOverrideKey(name, type);
	const override = overrideKey !== null ? getMessageByKey(overrideKey) : null;
	if (override !== null) {
		return override;
	}
	return getMessageByKey(userTypeRequestTextKeys[name], fallbacks[name]) ?? fallbacks[name];
}
